import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { extractList } from "../../../utils/response";
import { getErrorMessage } from "../../../utils/errorHandler";
import {
  useGetLeadsQuery,
  useLazyGetLeadsStatusQuery,
  useLazyGetUsersListQuery,
  useLazyGetUserByMultiRolesQuery,
} from "../../../store/api";
import { buildQueryParams } from "../utils";
import { useLazySelectOptions } from "../../../hooks/useLazySelectOptions";
import {
  useDashboardDrilldown,
  useDrilldownFilterSync,
  normalizeFilterKey,
} from "../../../hooks/useDashboardDrilldown";

export const useLeads = (canViewListLead = true) => {
  const {
    active: drilldownActive,
    params: drilldownParams,
    clear: clearDrilldown,
  } = useDashboardDrilldown();
  const [localLeads, setLocalLeads] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [noActivity, setNoActivity] = useState({
    no_activity_since_days: "",
  });
  const [filters, setFilters] = useState({
    status_id: "",
    assignee_id: "",
    author_id: "",
    payment_status: "",
  });
  const [sort, setSort] = useState({
    sort_direction: "desc",
  });
  const [processingId, setProcessingId] = useState(null);

  const queryParams = useMemo(
    () => ({
      ...buildQueryParams(
        { page: pagination.page, pageSize: pagination.pageSize },
        filters,
        sort,
        noActivity,
        search,
      ),
      ...drilldownParams,
    }),
    [
      pagination.page,
      pagination.pageSize,
      filters,
      sort,
      noActivity,
      search,
      drilldownParams,
    ],
  );

  const {
    data: leadsData,
    isLoading,
    isFetching,
    error: leadsError,
    refetch,
  } = useGetLeadsQuery(queryParams, { skip: !canViewListLead });

  const [fetchStatuses] = useLazyGetLeadsStatusQuery();
  const [fetchUsers] = useLazyGetUsersListQuery();
  const [fetchMultiRoleUsers] = useLazyGetUserByMultiRolesQuery();

  const statusSelect = useLazySelectOptions(fetchStatuses, {
    args: { "conditions[model]": "Lead", paginate: 0 },
    enabled: canViewListLead,
  });

  const usersSelect = useLazySelectOptions(fetchUsers, {
    // Without paginate: 0 the filter dropdown only ever shows the first page of users
    args: { paginate: 0 },
    mapResult: (res) => res?.list ?? extractList(res),
    enabled: canViewListLead,
  });

  const frontSellerSelect = useLazySelectOptions(fetchMultiRoleUsers, {
    args: { "roles[]": ["Closer", "Lead Gen"], paginate: 0 },
    mapResult: (res) => {
      const list = extractList(res?.data ?? res);
      return list.map((u) => ({
        id: u?.uuid ?? u?.id,
        name: u?.name,
      }));
    },
    enabled: canViewListLead,
  });

  // A status card opened this list: load the statuses and select that one in the Status filter.
  const { load: loadStatuses, options: statusOptionList } = statusSelect;
  useEffect(() => {
    if (drilldownParams.dd_status && canViewListLead) {
      loadStatuses().catch(() => {});
    }
  }, [drilldownParams.dd_status, canViewListLead, loadStatuses]);

  const drilldownStatusId = useMemo(() => {
    if (!drilldownParams.dd_status) return undefined;
    const key = normalizeFilterKey(drilldownParams.dd_status);
    return statusOptionList.find((s) => normalizeFilterKey(s.name) === key)?.id;
  }, [drilldownParams.dd_status, statusOptionList]);

  const detachDrilldown = useDrilldownFilterSync({
    active: drilldownActive,
    value: drilldownStatusId,
    apply: (id) => setFilters((prev) => ({ ...prev, status_id: id })),
    reset: () => setFilters((prev) => ({ ...prev, status_id: "" })),
    clear: clearDrilldown,
  });

  useEffect(() => {
    if (!canViewListLead) {
      toast.error("You do not have permission to view leads.");
    }
  }, [canViewListLead]);

  useEffect(() => {
    if (leadsError) {
      toast.error(getErrorMessage(leadsError) || "Failed to fetch leads");
    }
  }, [leadsError]);

  useEffect(() => {
    if (leadsData) {
      setLocalLeads(null);
      if (leadsData?.success && leadsData?.data?.pagination) {
        const nextTotal = leadsData.data.pagination?.total || 0;
        setPagination((prev) =>
          prev.total === nextTotal ? prev : { ...prev, total: nextTotal },
        );
      }
    }
  }, [leadsData]);

  const leads = localLeads ?? extractList(leadsData);
  const tableLoading = isLoading || isFetching;

  const fetchLeads = useCallback(() => {
    if (!canViewListLead) {
      toast.error("You do not have permission to view leads.");
      return;
    }
    return refetch();
  }, [canViewListLead, refetch]);

  /** Any change to the result set must send the user back to page 1 */
  const resetPage = () =>
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));

  const handleFilterChange = (field, value) => {
    if (field === "status_id") detachDrilldown();
    setFilters((prev) => ({ ...prev, [field]: value }));
    resetPage();
  };

  const handleSortChange = (fieldOrPartial) => {
    resetPage();
    if (fieldOrPartial != null && typeof fieldOrPartial === "object") {
      setSort((prev) => ({ ...prev, ...fieldOrPartial }));
      return;
    }
    const field = fieldOrPartial;
    setSort((prev) => ({
      sort_by: field,
      sort_direction:
        prev.sort_by === field && prev.sort_direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const clearFilters = () => {
    detachDrilldown();
    setFilters({
      status_id: "",
      assignee_id: "",
      author_id: "",
      payment_status: "",
    });
    setSort({ sort_by: "updated_at", sort_direction: "desc" });
    setNoActivity({ no_activity_since_days: "" });
    resetPage();
  };

  const handleNoActivityChange = (value) => {
    setNoActivity({ no_activity_since_days: value });
    resetPage();
  };

  const getActiveFiltersCount = () => {
    const filterCount = Object.values(filters).filter(
      (v) => v !== "" && v !== null && v !== undefined,
    ).length;
    return filterCount + (noActivity.no_activity_since_days ? 1 : 0);
  };

  const handleSearchInputChange = (e) => setSearchInput(e.target.value);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handlePageChange = (_, newPage) =>
    setPagination((p) => ({ ...p, page: newPage }));

  const handleRowsPerPageChange = (e) => {
    const sz = parseInt(e.target.value, 10);
    setPagination((p) => ({ ...p, page: 0, pageSize: sz }));
  };

  const setLeads = (updater) => {
    if (typeof updater === "function") {
      setLocalLeads((prev) => {
        const current = prev ?? extractList(leadsData);
        return updater(current);
      });
    }
  };

  return {
    leads,
    tableLoading,
    searchInput,
    search,
    pagination,
    filters,
    sort,
    noActivity,
    statusOptions: statusSelect.options,
    statusOptionsLoading: statusSelect.loading,
    loadStatusOptions: statusSelect.load,
    usersList: usersSelect.options,
    usersListLoading: usersSelect.loading,
    loadUsersList: usersSelect.load,
    frontSellerOptions: frontSellerSelect.options,
    frontSellerOptionsLoading: frontSellerSelect.loading,
    loadFrontSellerOptions: frontSellerSelect.load,
    processingId,
    setProcessingId,
    setLeads,
    fetchLeads,
    handleSearchInputChange,
    handleSearch,
    handleClearSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleSortChange,
    handleNoActivityChange,
    clearFilters,
    getActiveFiltersCount,
  };
};
